import {
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
  updateNotificationSettings,
} from '@/apiManager/notifications'

// In-app only: no polling. The badge refreshes on load, navigation (throttled),
// tab focus and when the bell opens.
const UNREAD_THROTTLE_MS = 30000
const LATEST_LIMIT = 10

// After an approve / reject the server recounts a request notification on a
// queue: ~5s later, or ~70s for a request still in its 60s batching buffer.
// Until then a fetch returns the old count, so the count the admin left is
// kept over it for a while, and the server is asked again once each delay
// has passed.
const LOCAL_COUNT_MS = 90000
const RECHECK_MS = [6000, 75000]
let recheckTimers = []

const clearRechecks = () => {
  recheckTimers.forEach(clearTimeout)
  recheckTimers = []
}

// A server row still showing more pending people than the admin left keeps
// the local count; once it has caught up, its own value wins.
const withLocalCounts = (items, localCounts) => {
  const now = Date.now()
  return items.map(item => {
    const local = localCounts[item.id]
    if (!local || local.until < now || !(item.pendingCount > local.pendingCount)) return item
    return {
      ...item,
      pendingCount: local.pendingCount,
      actionStatus: local.pendingCount > 0 ? item.actionStatus : 'resolved',
    }
  })
}

const emptyUnread = () => ({
  paused: false,
  total: 0,
  actionRequired: 0,
  byCategory: {},
  actionByCategory: {},
})

// Shared by every caller, so parallel triggers make one request.
let unreadRequest = null

export default {
  namespaced: true,
  state: {
    unread: emptyUnread(),
    unreadFetchedAt: 0,
    // The bell dropdown's list; the full page keeps its own.
    latest: [],
    latestLoaded: false,
    latestLoading: false,
    latestError: false,
    // The tab the bell last loaded, so a recheck reloads the same one.
    latestStatus: 'all',
    // { [notificationId]: { pendingCount, until } }, see LOCAL_COUNT_MS.
    localCounts: {},
  },
  getters: {
    badge: state => (state.unread.total > 99 ? '99+' : String(state.unread.total || '')),
    hasPendingAction: state => state.unread.actionRequired > 0,
    isPaused: state => !!state.unread.paused,
    // For lists kept outside the store (the notifications page).
    withLocalCounts: state => items => withLocalCounts(items, state.localCounts),
  },
  mutations: {
    setUnread(state, unread) {
      state.unread = { ...emptyUnread(), ...unread }
      state.unreadFetchedAt = Date.now()
    },
    setLatest(state, { items, status }) {
      state.latest = withLocalCounts(items, state.localCounts)
      state.latestLoaded = true
      state.latestStatus = status
    },
    setLatestLoading(state, value) {
      state.latestLoading = value
    },
    setLatestError(state, value) {
      state.latestError = value
    },
    setItemRead(state, { id, isRead }) {
      state.latest = state.latest.map(item => (item.id === id ? { ...item, isRead, newSinceRead: isRead ? 0 : item.newSinceRead } : item))
    },
    setAllRead(state, category) {
      state.latest = state.latest.map(item => (!category || item.category === category ? { ...item, isRead: true, newSinceRead: 0 } : item))
    },
    // Optimistic "mark all read": the badge clears at once, and comes back
    // from the snapshot if the server refuses.
    clearUnread(state, category) {
      if (!category) {
        state.unread = { ...state.unread, total: 0, byCategory: {} }
        return
      }
      const removed = state.unread.byCategory[category] || 0
      state.unread = {
        ...state.unread,
        total: Math.max(0, state.unread.total - removed),
        byCategory: { ...state.unread.byCategory, [category]: 0 },
      }
    },
    setLocalCount(state, { id, pendingCount }) {
      const now = Date.now()
      const kept = Object.fromEntries(Object.entries(state.localCounts).filter(([, local]) => local.until >= now))
      state.localCounts = { ...kept, [id]: { pendingCount, until: now + LOCAL_COUNT_MS } }
      state.latest = withLocalCounts(state.latest, state.localCounts)
    },
    restore(state, snapshot) {
      state.unread = snapshot.unread
      state.latest = snapshot.latest
    },
    reset(state) {
      state.unread = emptyUnread()
      state.unreadFetchedAt = 0
      state.latest = []
      state.latestLoaded = false
      state.latestLoading = false
      state.latestError = false
      state.latestStatus = 'all'
      state.localCounts = {}
    },
  },
  actions: {
    fetchUnread({ commit, state }, { force = false } = {}) {
      if (!localStorage.getItem('token')) return Promise.resolve()
      if (!force && Date.now() - state.unreadFetchedAt < UNREAD_THROTTLE_MS) return Promise.resolve()
      if (unreadRequest) return unreadRequest
      unreadRequest = getUnreadNotificationCount()
        .then(res => {
          // A failed refresh keeps the last value, silently.
          if (res?.statusCode === 200) commit('setUnread', res.data)
        })
        .finally(() => {
          unreadRequest = null
        })
      return unreadRequest
    },
    async fetchLatest({ commit }, { status = 'all' } = {}) {
      commit('setLatestLoading', true)
      commit('setLatestError', false)
      try {
        const res = await getNotifications({ limit: LATEST_LIMIT, offset: 0, status })
        if (res?.statusCode === 200) {
          commit('setLatest', { items: res.data || [], status })
        } else {
          commit('setLatestError', true)
        }
      } catch (e) {
        commit('setLatestError', true)
      } finally {
        commit('setLatestLoading', false)
      }
    },
    // Optimistic; rolled back if the server refuses.
    async markRead({ commit, dispatch }, item) {
      if (!item || item.isRead) return true
      commit('setItemRead', { id: item.id, isRead: true })
      const res = await markNotificationRead(item.id)
      if (res?.statusCode !== 200) {
        commit('setItemRead', { id: item.id, isRead: false })
        return false
      }
      dispatch('fetchUnread', { force: true })
      return true
    },
    async markAllRead({ commit, dispatch, state }, category) {
      const snapshot = { unread: state.unread, latest: state.latest }
      commit('setAllRead', category)
      commit('clearUnread', category)
      const res = await markAllNotificationsRead(category)
      if (res?.statusCode !== 200) {
        commit('restore', snapshot)
        return false
      }
      dispatch('fetchUnread', { force: true })
      return true
    },
    // The bell's "Notifications are paused · Turn on".
    async resume({ dispatch }) {
      const res = await updateNotificationSettings({ paused: false })
      if (res?.statusCode !== 200) return false
      dispatch('fetchUnread', { force: true })
      return true
    },
    // Join requests were approved / rejected: in the people pop-up (`item` is
    // the notification with its new pending count) or on a requests page (no
    // item). The row updates now; the badge and the bell follow the server.
    requestsHandled({ commit, dispatch, state }, item) {
      if (item?.id) commit('setLocalCount', { id: item.id, pendingCount: Math.max(0, item.pendingCount || 0) })
      clearRechecks()
      recheckTimers = RECHECK_MS.map(delay => setTimeout(() => {
        dispatch('fetchUnread', { force: true })
        if (state.latestLoaded) dispatch('fetchLatest', { status: state.latestStatus })
      }, delay))
    },
    reset({ commit }) {
      clearRechecks()
      commit('reset')
    },
  },
}
